import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { qcAPI } from '../api';
import {
  Search, Star, Camera, X, Filter, ChevronRight,
  ShieldCheck, ShieldAlert, User, Scissors, TrendingUp,
} from 'lucide-react';

const FILTERS = [
  { id: 'all', label: 'All', color: '#0F0F0F' },
  { id: 'pending', label: 'Pending QC', color: '#D4AF37' },
  { id: 'approved', label: 'Approved', color: '#166534' },
  { id: 'low_confidence', label: 'Low Confidence', color: '#991B1B' },
  { id: 'high_risk', label: 'High Risk', color: '#991B1B' },
];

const ALTERATION_LABEL = {
  none: { label: 'No Alteration', color: '#166534' },
  minor: { label: 'Minor', color: '#D4AF37' },
  major: { label: 'Major', color: '#991B1B' },
};

const AdminQC = () => {
  const [filter, setFilter] = useState('all');
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const [view, setView] = useState('orders'); // 'orders' | 'tailors'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, tstats] = await Promise.all([
        qcAPI.adminList(filter),
        qcAPI.tailorStats(),
      ]);
      setOrders(list); setStats(tstats);
    } catch {/* silent */}
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const filteredOrders = useMemo(() => {
    if (!search) return orders;
    const s = search.toLowerCase();
    return orders.filter(o =>
      (o.order_id || '').toLowerCase().includes(s) ||
      (o.customer_name || '').toLowerCase().includes(s) ||
      (o.tailor_name || '').toLowerCase().includes(s) ||
      (o.product || '').toLowerCase().includes(s)
    );
  }, [orders, search]);

  const StatusBadge = ({ status }) => {
    const map = {
      qc_pending: { bg: 'rgba(212,175,55,0.15)', color: '#D4AF37', label: 'Pending' },
      qc_approved: { bg: 'rgba(22,101,52,0.1)', color: '#166534', label: 'Approved' },
      ready: { bg: 'rgba(22,101,52,0.08)', color: '#166534', label: 'Ready' },
      shipped: { bg: 'rgba(22,101,52,0.08)', color: '#166534', label: 'Shipped' },
      delivered: { bg: 'rgba(22,101,52,0.08)', color: '#166534', label: 'Delivered' },
      completed: { bg: 'rgba(22,101,52,0.08)', color: '#166534', label: 'Completed' },
    };
    const s = map[status] || { bg: '#F5F5F5', color: '#595959', label: status };
    return (
      <span className="px-1.5 py-px text-[9px] uppercase tracking-[0.05em]"
        style={{ backgroundColor: s.bg, color: s.color }}>{s.label}</span>
    );
  };

  const Stars = ({ n = 0 }) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className="w-3 h-3" strokeWidth={1.5}
          style={{ color: i <= n ? '#D4AF37' : '#E5E5E5', fill: i <= n ? '#D4AF37' : 'transparent' }} />
      ))}
      <span className="text-[10px] ml-1" style={{ color: '#595959' }}>{n || '-'}/5</span>
    </div>
  );

  return (
    <div data-testid="admin-qc" className="space-y-3">
      {/* View toggle: Orders vs Tailors */}
      <div className="flex gap-1">
        <button
          data-testid="qc-view-orders"
          onClick={() => setView('orders')}
          className="flex-1 py-2 text-[10px] tracking-[0.1em] uppercase border"
          style={{
            backgroundColor: view === 'orders' ? '#0F0F0F' : '#FFF',
            color: view === 'orders' ? '#FFF' : '#595959',
            borderColor: view === 'orders' ? '#0F0F0F' : '#E5E5E5',
          }}>
          <ShieldCheck className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> QC Orders
        </button>
        <button
          data-testid="qc-view-tailors"
          onClick={() => setView('tailors')}
          className="flex-1 py-2 text-[10px] tracking-[0.1em] uppercase border"
          style={{
            backgroundColor: view === 'tailors' ? '#0F0F0F' : '#FFF',
            color: view === 'tailors' ? '#FFF' : '#595959',
            borderColor: view === 'tailors' ? '#0F0F0F' : '#E5E5E5',
          }}>
          <TrendingUp className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> Tailor Performance
        </button>
      </div>

      {view === 'orders' && (
        <>
          {/* Filter chips */}
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
            {FILTERS.map(f => (
              <button
                key={f.id}
                data-testid={`qc-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className="flex-shrink-0 px-3 py-1.5 text-[10px] tracking-[0.05em] uppercase border whitespace-nowrap"
                style={{
                  backgroundColor: filter === f.id ? f.color : '#FFF',
                  color: filter === f.id ? '#FFF' : '#595959',
                  borderColor: filter === f.id ? f.color : '#E5E5E5',
                }}>
                <Filter className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> {f.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} />
            <input
              data-testid="qc-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by order, customer, tailor..."
              className="w-full pl-9 pr-3 py-2 text-[12px] bg-white border focus:outline-none"
              style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
          </div>

          {loading && <p className="text-center text-[12px] py-8" style={{ color: '#595959' }}>Loading QC records…</p>}
          {!loading && filteredOrders.length === 0 && (
            <div className="text-center py-10">
              <ShieldAlert className="w-8 h-8 mx-auto mb-2" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
              <p className="text-[12px]" style={{ color: '#595959' }}>No QC records match this filter</p>
            </div>
          )}

          {/* Order cards */}
          {filteredOrders.map(o => {
            const qc = o.qc || {};
            const alt = ALTERATION_LABEL[qc.alteration_prediction] || null;
            return (
              <button
                key={o.id}
                data-testid={`qc-card-${o.order_id}`}
                onClick={() => setSelected(o)}
                className="w-full bg-white border p-3 text-left transition-colors hover:bg-black/[0.02]"
                style={{ borderColor: '#E5E5E5' }}>
                <div className="flex items-start justify-between mb-1.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{o.order_id}</p>
                    <StatusBadge status={o.status} />
                    {alt && (
                      <span className="px-1.5 py-px text-[9px] tracking-[0.05em] uppercase"
                        style={{ backgroundColor: `${alt.color}18`, color: alt.color }}>{alt.label}</span>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
                </div>
                <p className="text-[11px] mb-1" style={{ color: '#595959' }}>
                  <User className="w-3 h-3 inline mr-1" strokeWidth={1.5} /> {o.customer_name || '—'}
                  {' · '}
                  <Scissors className="w-3 h-3 inline mr-1 ml-1" strokeWidth={1.5} /> {o.tailor_name || 'Unassigned'}
                </p>
                <p className="text-[11px] mb-2" style={{ color: '#595959' }}>{o.product}</p>
                <div className="flex items-center justify-between">
                  <Stars n={qc.confidence_score || 0} />
                  <div className="flex items-center gap-1 text-[10px]" style={{ color: '#595959' }}>
                    {qc.photos && Object.keys(qc.photos).length > 0 && (
                      <><Camera className="w-3 h-3" strokeWidth={1.5} /> {Object.keys(qc.photos).length} photos</>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </>
      )}

      {view === 'tailors' && (
        <div className="space-y-2">
          {loading && <p className="text-center text-[12px] py-8" style={{ color: '#595959' }}>Loading…</p>}
          {!loading && stats.length === 0 && (
            <div className="text-center py-10">
              <TrendingUp className="w-8 h-8 mx-auto mb-2" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
              <p className="text-[12px]" style={{ color: '#595959' }}>No QC-scored tailors yet</p>
            </div>
          )}
          {stats.map(s => (
            <div key={s.tailor_id} data-testid={`tailor-stat-${s.tailor_id}`} className="bg-white border p-3" style={{ borderColor: '#E5E5E5' }}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{s.tailor_name}</p>
                  <p className="text-[10px]" style={{ color: '#595959' }}>{s.city || '—'} · {s.total_orders} lifetime orders</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] tracking-[0.1em] uppercase" style={{ color: '#595959' }}>QC Done</p>
                  <p className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>{s.qc_completed}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center border-t pt-2" style={{ borderColor: '#F0F0F0' }}>
                <div>
                  <p className="text-[9px] tracking-[0.05em] uppercase" style={{ color: '#595959' }}>Confidence</p>
                  <p className="text-[13px] font-medium" style={{ color: s.avg_confidence >= 4 ? '#166534' : s.avg_confidence >= 3 ? '#D4AF37' : '#991B1B' }}>
                    {s.avg_confidence || '—'}/5
                  </p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.05em] uppercase" style={{ color: '#595959' }}>Alt. Rate</p>
                  <p className="text-[13px] font-medium" style={{ color: s.predicted_alteration_rate < 20 ? '#166534' : s.predicted_alteration_rate < 50 ? '#D4AF37' : '#991B1B' }}>
                    {s.predicted_alteration_rate}%
                  </p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.05em] uppercase" style={{ color: '#595959' }}>Cx Rating</p>
                  <div className="flex items-center justify-center gap-1">
                    <Star className="w-3 h-3" strokeWidth={1.5} style={{ color: '#D4AF37', fill: '#D4AF37' }} />
                    <span className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{s.customer_rating || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QC Detail Modal */}
      {selected && (
        <div data-testid="qc-detail-modal" className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center"
          style={{ backgroundColor: 'rgba(15,15,15,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg h-[90vh] sm:h-[85vh] bg-white flex flex-col shadow-2xl overflow-hidden"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: '#E5E5E5' }}>
              <div>
                <p className="text-[10px] tracking-[0.15em] uppercase" style={{ color: '#D4AF37' }}>QC Record</p>
                <h2 className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
                  {selected.order_id}
                </h2>
              </div>
              <button data-testid="qc-detail-close" onClick={() => setSelected(null)} className="p-2 -mr-2">
                <X className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <p className="tracking-[0.1em] uppercase text-[9px]" style={{ color: '#595959' }}>Customer</p>
                  <p style={{ color: '#0F0F0F' }}>{selected.customer_name || '—'}</p>
                </div>
                <div>
                  <p className="tracking-[0.1em] uppercase text-[9px]" style={{ color: '#595959' }}>Tailor</p>
                  <p style={{ color: '#0F0F0F' }}>{selected.tailor_name || '—'}</p>
                </div>
                <div>
                  <p className="tracking-[0.1em] uppercase text-[9px]" style={{ color: '#595959' }}>Product</p>
                  <p style={{ color: '#0F0F0F' }}>{selected.product}</p>
                </div>
                <div>
                  <p className="tracking-[0.1em] uppercase text-[9px]" style={{ color: '#595959' }}>Status</p>
                  <p style={{ color: '#0F0F0F' }}><StatusBadge status={selected.status} /></p>
                </div>
              </div>

              {selected.qc && selected.qc.status === 'approved' && (
                <>
                  {/* Confidence + Alteration */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="border p-3" style={{ borderColor: '#E5E5E5' }}>
                      <p className="text-[9px] tracking-[0.1em] uppercase mb-1" style={{ color: '#595959' }}>Confidence</p>
                      <Stars n={selected.qc.confidence_score} />
                    </div>
                    <div className="border p-3" style={{ borderColor: '#E5E5E5' }}>
                      <p className="text-[9px] tracking-[0.1em] uppercase mb-1" style={{ color: '#595959' }}>Alteration</p>
                      {(() => {
                        const alt = ALTERATION_LABEL[selected.qc.alteration_prediction];
                        return alt ? (
                          <p className="text-[11px] font-medium" style={{ color: alt.color }}>{alt.label}</p>
                        ) : <p className="text-[11px]" style={{ color: '#595959' }}>—</p>;
                      })()}
                    </div>
                  </div>

                  {/* Photos */}
                  {selected.qc.photos && (
                    <div>
                      <p className="text-[10px] tracking-[0.1em] uppercase mb-2" style={{ color: '#595959' }}>Photos</p>
                      <div className="grid grid-cols-2 gap-2">
                        {['front', 'back', 'side', 'detail'].map(slot => (
                          selected.qc.photos[slot] && (
                            <button
                              key={slot}
                              data-testid={`qc-photo-${slot}`}
                              onClick={() => setZoomPhoto({ src: selected.qc.photos[slot], label: slot })}
                              className="aspect-square relative overflow-hidden border"
                              style={{ borderColor: '#E5E5E5' }}>
                              <img src={selected.qc.photos[slot]} alt={slot}
                                className="w-full h-full object-cover" />
                              <div className="absolute bottom-0 left-0 right-0 py-1 flex items-center justify-center"
                                style={{ backgroundColor: 'rgba(15,15,15,0.7)' }}>
                                <span className="text-[9px] text-white tracking-[0.1em] uppercase">{slot}</span>
                              </div>
                            </button>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Checklist recap */}
                  {selected.qc.checklist && (
                    <div>
                      <p className="text-[10px] tracking-[0.1em] uppercase mb-2" style={{ color: '#595959' }}>Checklist</p>
                      <div className="space-y-2">
                        {Object.entries(selected.qc.checklist).map(([group, items]) => (
                          <div key={group} className="border p-2" style={{ borderColor: '#E5E5E5' }}>
                            <p className="text-[10px] tracking-[0.05em] uppercase mb-1" style={{ color: '#0F0F0F' }}>{group}</p>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                              {Object.entries(items).map(([k, v]) => (
                                <p key={k} className="text-[10px]" style={{ color: v ? '#166534' : '#991B1B' }}>
                                  {v ? '✓' : '✗'} {k}
                                </p>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              {(!selected.qc || selected.qc.status !== 'approved') && (
                <div className="text-center py-6">
                  <ShieldAlert className="w-8 h-8 mx-auto mb-2" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                  <p className="text-[12px]" style={{ color: '#595959' }}>QC is still pending completion by the tailor.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {zoomPhoto && (
        <div data-testid="qc-photo-zoom" className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }} onClick={() => setZoomPhoto(null)}>
          <img src={zoomPhoto.src} alt={zoomPhoto.label} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
};

export default AdminQC;
